/**
 * Retry logic manager for Bifrost operations
 */

import { RetryConfig } from "../types/protocol";
import { ErrorCode, RpcCallError } from "../types/errors";

export class RetryManager {
  private readonly config: Required<RetryConfig>;

  constructor(config: RetryConfig) {
    this.config = {
      maxRetries: config.maxRetries ?? 3,
      retryDelay: config.retryDelay ?? 1000,
      retryBackoff: config.retryBackoff ?? 2,
      retryableErrors: config.retryableErrors ?? [
        ErrorCode.CONNECTION_FAILED,
        ErrorCode.CONNECTION_TIMEOUT,
        ErrorCode.INTERNAL_ERROR,
      ],
    };
  }

  /**
   * Execute operation with retry logic
   */
  async withRetry<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: Error | undefined;
    let attempt = 0;

    while (attempt <= this.config.maxRetries) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        // Don't retry on last attempt or if error is not retryable
        if (
          attempt === this.config.maxRetries ||
          !this.isRetryableError(error as Error)
        ) {
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = this.calculateDelay(attempt);
        await this.sleep(delay);

        attempt++;
      }
    }

    throw lastError;
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(error: Error): boolean {
    if (error instanceof RpcCallError) {
      return this.config.retryableErrors.includes(error.code);
    }

    // Generic errors are not retryable by default
    return false;
  }

  /**
   * Calculate delay with exponential backoff
   */
  private calculateDelay(attempt: number): number {
    return this.config.retryDelay * Math.pow(this.config.retryBackoff, attempt);
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get current retry configuration
   */
  getConfig(): Required<RetryConfig> {
    return { ...this.config };
  }

  /**
   * Check if specific error code is retryable
   */
  isErrorCodeRetryable(code: number): boolean {
    return this.config.retryableErrors.includes(code);
  }
}
